import React, { useState, useEffect } from 'react';
import { TOKENS } from '../tokens';
import api from '../api/client';
import { Clock, CheckSquare, Plus, Briefcase } from 'lucide-react';

export function EmployeeTimesheetScreen({ emp }) {
  const [allocations, setAllocations] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [weekStart, setWeekStart] = useState('');
  const [projectId, setProjectId] = useState('');
  const [hours, setHours] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
    // Default to current week's Monday
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    setWeekStart(monday.toISOString().split('T')[0]);
  }, []);

  const fetchData = async () => {
    try {
      const [allocRes, tsRes] = await Promise.all([
        api.get('/team/allocations/me'),
        api.get('/team/timesheets/me')
      ]);
      setAllocations(allocRes.data);
      setTimesheets(tsRes.data);
      if (allocRes.data.length > 0) {
        setProjectId(allocRes.data[0].project_id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectId || !hours || !weekStart) return alert("Please fill all fields");
    setSubmitting(true);
    try {
      await api.post('/team/timesheets', { week_start: weekStart, project_id: projectId, hours });
      alert("Timesheet submitted!");
      setShowForm(false);
      setHours('');
      fetchData();
    } catch (err) {
      alert("Failed to submit timesheet: " + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '16px 20px', paddingBottom: 100 }}>
      <h2 style={{ margin: '0 0 20px 0', fontFamily: 'Fraunces', color: TOKENS.navyDeep, fontSize: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Clock color={TOKENS.gold} /> My Timesheets
      </h2>

      {loading ? <p>Loading...</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          
          {/* Allocations Summary */}
          <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #eee' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 16, color: TOKENS.navyDeep, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Briefcase size={18} /> My Project Allocations
            </h3>
            {allocations.length === 0 ? (
              <p style={{ color: '#888', margin: 0, fontSize: 14 }}>Not currently assigned to any project.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {allocations.map(a => (
                  <div key={a.project_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600, color: TOKENS.navyDeep }}>{a.name}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>Role: {a.role_on_project || 'Member'}</div>
                    </div>
                    <div style={{ background: TOKENS.navySoft, color: '#fff', padding: '4px 12px', borderRadius: 12, fontSize: 13, fontWeight: 700 }}>
                      {a.allocation_pct}%
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit New Timesheet */}
          {!showForm ? (
            <button 
              onClick={() => setShowForm(true)}
              style={{
                background: TOKENS.gold, color: TOKENS.navyDeep, border: 'none', padding: 16, borderRadius: 12,
                fontWeight: 700, fontSize: 16, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, cursor: 'pointer', width: '100%'
              }}
            >
              <Plus size={20} /> Log Hours
            </button>
          ) : (
            <form onSubmit={handleSubmit} style={{ background: '#fff', padding: 20, borderRadius: 16, border: '1px solid #eee' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: 16 }}>Log Weekly Hours</h3>
              
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4, color: '#666' }}>Week Commencing (Monday)</label>
                <input 
                  type="date" 
                  value={weekStart} 
                  onChange={e => setWeekStart(e.target.value)} 
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                  required 
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4, color: '#666' }}>Project</label>
                <select 
                  value={projectId} 
                  onChange={e => setProjectId(e.target.value)}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                  required
                >
                  {allocations.map(a => (
                    <option key={a.project_id} value={a.project_id}>{a.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4, color: '#666' }}>Total Hours Worked</label>
                <input 
                  type="number" 
                  min="0" step="0.5" max="100"
                  value={hours} 
                  onChange={e => setHours(e.target.value)} 
                  placeholder="e.g. 40"
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                  required 
                />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" onClick={() => setShowForm(false)} style={{ flex: 1, padding: 12, borderRadius: 8, border: '1px solid #ccc', background: '#fff', color: '#666', fontWeight: 600 }}>Cancel</button>
                <button type="submit" disabled={submitting} style={{ flex: 1, padding: 12, borderRadius: 8, border: 'none', background: TOKENS.navyDeep, color: '#fff', fontWeight: 600 }}>
                  {submitting ? 'Saving...' : 'Submit'}
                </button>
              </div>
            </form>
          )}

          {/* History */}
          <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #eee' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 16, color: TOKENS.navyDeep }}>Timesheet History</h3>
            
            {timesheets.length === 0 ? (
              <p style={{ color: '#888', margin: 0, fontSize: 14 }}>No timesheets submitted yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {timesheets.map(ts => (
                  <div key={ts.id} style={{ 
                    border: '1px solid #eee', borderRadius: 12, padding: 12,
                    background: ts.approved_by ? '#F8FCF9' : '#fff'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontWeight: 600, color: TOKENS.navyDeep }}>Week: {ts.week_start}</div>
                      <div style={{ fontSize: 12, color: ts.approved_by ? '#1E8E3E' : '#E65100', fontWeight: 600 }}>
                        {ts.approved_by ? 'Approved' : 'Pending'}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, color: '#555', marginTop: 8 }}>
                      Project: {ts.project_name} <br/>
                      Hours: <b>{ts.hours}h</b>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
        </div>
      )}
    </div>
  );
}
